import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useChineseLang } from '../../i18n/chinese'
import { CORRECTIONS } from 'routes/routeConstant'
import { DiscussionNotification, fetchNotifications, markNotificationsRead } from './discussionApi'
import { notificationLine, notificationPath, replyTime } from './discussionLogic'
import { Avatar, BellIcon, NOTIFICATIONS_CHANGED } from './DiscussionBits'
import './discussion.css'

/** Refresh at most this often when the tab regains focus; opening the list always refreshes. */
const REFRESH_MS = 60_000

/**
 * The bell in the header: replies to the learner's answer, replies in threads they joined, likes,
 * words other learners added from their answer, and corrections their teacher sent back. Asks on mount, when the tab comes back into view
 * and when the list opens; no polling, since replies arrive minutes or hours apart.
 */
const NotificationBell: React.FC = () => {
  const { t } = useTranslation('discussion')
  const zhLang = useChineseLang()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<DiscussionNotification[]>([])
  const [unread, setUnread] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const lastLoad = useRef(0)
  const slotRef = useRef<HTMLDivElement>(null)

  const load = useCallback(() => {
    lastLoad.current = Date.now()
    fetchNotifications()
      .then((res) => {
        setItems(res.notifications || [])
        setUnread(res.unreadCount || 0)
      })
      .catch(() => undefined)
      .finally(() => setLoaded(true))
  }, [])

  // Opening the list is reading it: the count clears and the server marks everything read. The green
  // dots stay on what was new until the list closes, so the learner can still see which ones those were.
  const close = useCallback(() => {
    setOpen(false)
    setItems((list) => list.map((i) => ({ ...i, read: true })))
  }, [])

  useEffect(() => {
    load()
    const onFocus = () => {
      if (Date.now() - lastLoad.current > REFRESH_MS) load()
    }
    window.addEventListener('focus', onFocus)
    window.addEventListener(NOTIFICATIONS_CHANGED, load)
    return () => {
      window.removeEventListener('focus', onFocus)
      window.removeEventListener(NOTIFICATIONS_CHANGED, load)
    }
  }, [load])

  useEffect(() => close(), [pathname, close])

  // Reading the corrections page is reading the corrections news, however the learner got there:
  // the bell, the header link or the email.
  const onCorrections = pathname.startsWith(CORRECTIONS)
  useEffect(() => {
    if (!onCorrections) return
    const fresh = items.filter((i) => i.kind === 'correction_ready' && !i.read).length
    if (!fresh) return
    setItems((list) => list.map((i) => (i.kind === 'correction_ready' ? { ...i, read: true } : i)))
    setUnread((count) => Math.max(0, count - fresh))
    markNotificationsRead(undefined, 'correction_ready')
  }, [onCorrections, items])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!slotRef.current?.contains(e.target as Node)) close()
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close()
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, close])

  const toggle = () => {
    if (open) {
      close()
      return
    }
    setOpen(true)
    lastLoad.current = Date.now()
    fetchNotifications()
      .then((res) => {
        setItems(res.notifications || [])
        setUnread(0)
        if (res.unreadCount > 0) markNotificationsRead()
      })
      .catch(() => undefined)
      .finally(() => setLoaded(true))
  }

  const openItem = (n: DiscussionNotification) => {
    close()
    navigate(notificationPath(n))
  }

  const label = unread > 0 ? t('notifications.labelWithCount', { count: unread }) : t('notifications.title')
  return (
    <div className='header_bell_slot' ref={slotRef}>
      <button type='button' className='header_bell_btn' aria-label={label} aria-expanded={open} onClick={toggle}>
        <BellIcon />
        {unread > 0 && (
          <span className='corrections_badge' aria-hidden='true'>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className='dc-bell-panel' role='dialog' aria-label={t('notifications.title')}>
          <div className='dc-bell-top'>
            <b>{t('notifications.title')}</b>
          </div>
          {loaded && items.length === 0 && <div className='dc-bell-empty'>{t('notifications.empty')}</div>}
          {items.map((n) => (
            <button key={n.id} type='button' className={`dc-note${n.read ? ' read' : ''}`} onClick={() => openItem(n)}>
              <Avatar name={n.kind === 'words_added' ? String(n.count || 1) : n.actorName} teacher={n.actorIsTeacher} />
              <span>
                <span className='dc-note-line'>{notificationLine(n)}</span>
                {n.preview && n.kind !== 'words_added' && (
                  <span className='dc-note-q' lang={zhLang}>
                    {n.preview}
                  </span>
                )}
                <span className='dc-note-meta'>
                  {n.storyTitle && (
                    <span className='zh' lang={zhLang}>
                      {n.storyTitle}
                    </span>
                  )}
                  {n.storyTitle && replyTime(n.at) ? ' · ' : ''}
                  {replyTime(n.at)}
                </span>
              </span>
              <span className='dc-dot' aria-hidden='true' />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default NotificationBell
